import { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import dayjs from 'dayjs'

import { ProcessState } from '@makyoapp/makyo-sdk/models/models'
import {
  ColumnTypes,
  ITableQuery,
  ITableSnapshotSettings,
  TableQueryOp,
  TableSortBy,
  TableSortOrder,
} from '@makyoapp/makyo-sdk/models/Table'
import {
  ColumnAdapter,
  DATE_OPERATOR_WITH_RANGE_RHS,
  DATE_OPERATOR_WITH_RHS,
  DefaultConditionOperator,
  fromSdkColumnList,
  fromSdkTable,
} from '@makyoapp/makyo-common'
import { Asc16, Desc16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import {
  ButtonColor,
  Divider,
  Field,
  Modal,
  ModalGridFooter,
  SelectDropdown,
  SimpleInput,
  SingleIconSelect,
} from '@makyoapp/makyo-ui'

import { DEFAULT_LANGUAGE, IDBTable } from '@/core/db'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useSingleProjectContext } from '@/context/SingleProjectContext'
import { useIsProjectDb } from '@/hooks/useIsProjectDb'
import { useParamId } from '@/hooks/useParamId'
import { validateTableName } from '@/shared/ag-grid'
import { getLookupResolvedColumn } from '@/shared/condition'
import { ORG_DATABASE_ROUTE, ORG_ROUTE, PROJECT_ROUTE, SHOW_SEARCH_LIMIT } from '@/shared/constants'
import { EVENT_KEYS } from '@/shared/custom-event'
import { appIconKeys } from '@/shared/icons'
import { ColumnsSelectToggle } from '@/components/ColumnsSelectToggle'
import { SnapshotFilterCondition } from '@/components/Condition/SnapshotFilterCondition'
import { Lbl } from '@/components/GridV2/components/fields/shared'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'
import { RenderColumn } from '@/components/RenderColumn'
import { TablesDropdownV2 } from '@/components/TablesDropdownV2'
import { TzDd } from '@/components/TzDd'

const REFRESH_RATE_OPTIONS = [
  { value: 60 * 1000, label: '1m' },
  { value: 60 * 5 * 1000, label: '5m' },
  { value: 60 * 10 * 1000, label: '10m' },
  { value: 60 * 15 * 1000, label: '15m' },
  { value: 60 * 30 * 1000, label: '30m' },
  { value: 60 * 45 * 1000, label: '45m' },
  { value: 60 * 60 * 1000, label: '1h' },
]

const EXCLUDED_COLUMN_TYPES = [
  ColumnTypes.Connection,
  ColumnTypes.Lookup,
  ColumnTypes.Math,
  ColumnTypes.Template,
  ColumnTypes.Function,
  ColumnTypes.SingleCell,
]

const LIMIT_OPTIONS = [10, 25, 50, 100, 250, 500, 1000, 5000]

const initialQuerySettings = {
  tableName: '',
  refreshRate: REFRESH_RATE_OPTIONS[1].value,
  sourceTable: '',
  columns: [] as string[],
  limit: 100,
  sortBy: TableSortBy.Default,
  sortType: 'asc' as 'asc' | 'desc',
}

const initialQueryFilter: ITableQuery = {
  _Op: TableQueryOp.AND,
  _Statements: [],
}

const createInitialQuerySettings = (tablesMap: Map<string, IDBTable>, defaultTable?: IDBTable) => {
  if (defaultTable) {
    const settings = defaultTable.settings._NatureSettings as ITableSnapshotSettings
    const sourceTable = tablesMap.get(settings?._Source)
    if (settings && sourceTable) {
      return {
        tableName: defaultTable.settings._Name,
        refreshRate: settings._RefreshRate,
        sourceTable: sourceTable.id || '',
        columns: settings._Columns || [],
        limit: settings._Limit,
        sortBy:
          settings._Sort._By === TableSortBy.Default
            ? ''
            : settings._Sort._By === TableSortBy.Random
              ? '#random#'
              : settings._Sort._Column,
        sortType: settings._Sort?._Order as 'asc' | 'desc',
      }
    }
  }

  return initialQuerySettings
}

const createInitialQueryFilter = (defaultTable?: IDBTable): ITableQuery => {
  if (defaultTable) {
    const settings = defaultTable.settings._NatureSettings as ITableSnapshotSettings
    if (settings) {
      const query = settings._Query || { _Op: 'AND', _Statements: [] }
      return {
        _Op: query._Op,
        _Statements: query._Statements.map((statement) => ({
          _Column: statement._Column,
          _Op: statement._Op,
          _Value1: statement._Value1 === 'string' ? { isCustom: true, value: '' } : statement._Value1,
          _Value2: statement._Value2 === 'string' ? { isCustom: true, value: '' } : statement._Value2,
          Options: null as any,
        })),
      }
    }
  }

  return initialQueryFilter
}

const getInitialTimezone = (activeTable?: IDBTable) => {
  return activeTable ? activeTable.settings._Tz : dayjs.tz.guess()
}

/**
 * Check if a CondArg is a custom date value (not a keyword like ###today###)
 */
function isCustomDateValue(arg: unknown): arg is { isCustom: boolean | string; value: string } {
  if (!arg || typeof arg === 'string') return false
  const obj = arg as { isCustom?: boolean | string; value?: string }
  if (!obj.isCustom || !obj.value || typeof obj.value !== 'string') return false
  if (obj.value.startsWith('###')) return false
  return true
}

/**
 * Convert a date string from one timezone interpretation to UTC.
 * DateTimeInput works in UTC internally, so the stored value looks like UTC but
 * actually represents a time in the table's timezone.
 */
function dateToUtc(dateStr: string, tz: string): string {
  const stripped = dateStr.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '')
  const converted = dayjs.tz(stripped, tz)
  return converted.isValid() ? converted.utc().toISOString() : dateStr
}

/**
 * Convert a UTC date string to table timezone for display.
 * Returns a "fake UTC" string so DateTimeInput displays the correct local time.
 */
function dateFromUtc(dateStr: string, tz: string): string {
  const utcDate = dayjs.utc(dateStr)
  if (!utcDate.isValid()) return dateStr
  return utcDate.tz(tz).format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z'
}

/**
 * Convert date values in query filter statements.
 * direction: 'toUtc' = table tz → UTC (for saving), 'fromUtc' = UTC → table tz (for display)
 */
function convertQueryDates(query: ITableQuery, tz: string, direction: 'toUtc' | 'fromUtc'): ITableQuery {
  const convert = direction === 'toUtc' ? dateToUtc : dateFromUtc

  return {
    _Op: query._Op,
    _Statements: query._Statements.map((stmt) => {
      const op = stmt._Op as DefaultConditionOperator
      const isDateOp = DATE_OPERATOR_WITH_RHS.includes(op) || DATE_OPERATOR_WITH_RANGE_RHS.includes(op)
      if (!isDateOp) return stmt

      return {
        ...stmt,
        _Value1: isCustomDateValue(stmt._Value1)
          ? { ...stmt._Value1, value: convert(stmt._Value1.value, tz) }
          : stmt._Value1,
        _Value2: isCustomDateValue(stmt._Value2)
          ? { ...stmt._Value2, value: convert(stmt._Value2.value, tz) }
          : stmt._Value2,
      }
    }),
  }
}

/**
 * Create/Update Query Table Modal
 */
export function QueryBuilderModal() {
  const { t } = useTranslation()
  const { dbId, tables, tablesMap, showQueryTable, setShowQueryTable, dbClient, setShowCreateTableOptions } =
    useDbClientContext()
  const { activeTable } = useGridV2Context()

  const isProject = useIsProjectDb()
  const { project } = useSingleProjectContext()
  const navigate = useNavigate()
  const orgId = useParamId('org')

  const showModal = showQueryTable === 'create' || showQueryTable === 'edit'
  const isEdit = showQueryTable === 'edit'

  // local state
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [nameError, setNameError] = useState('')
  const [loading, setLoading] = useState(false)

  // form state
  const [querySettings, setQuerySettings] = useState(() =>
    createInitialQuerySettings(tablesMap, isEdit ? activeTable : undefined)
  )
  const [queryFilter, setQueryFilter] = useState(() => {
    const filter = createInitialQueryFilter(isEdit ? activeTable : undefined)
    if (isEdit) {
      const tz = getInitialTimezone(activeTable)
      return convertQueryDates(filter, tz, 'fromUtc')
    }
    return filter
  })
  const [timezone, setTimezone] = useState(() => getInitialTimezone(isEdit ? activeTable : undefined))

  /**
   * Source table to query table
   */
  const sourceTable = useMemo(() => {
    if (!tablesMap.size || querySettings.sourceTable === '') {
      return
    }
    return tablesMap.get(querySettings.sourceTable)
  }, [tablesMap, querySettings.sourceTable])

  /**
   * create source column order map
   */
  const sourceColumnOrderMap = useMemo(() => {
    if (!sourceTable) return {}
    return sourceTable.GetColumns().reduce(
      (acc, c, idx) => {
        acc[c.id] = idx
        return acc
      },
      {} as Record<string, number>
    )
  }, [sourceTable])

  /**
   * handle column change
   * it will sort new columns based on source column order
   */
  const handleColumnChange = (newColumns: string[]) => {
    const sortedColumns = newColumns.sort((a, b) => {
      return sourceColumnOrderMap[a] - sourceColumnOrderMap[b]
    })
    setQuerySettings((prev) => ({ ...prev, columns: sortedColumns }))
  }

  /**
   * filter column options
   */
  const filterColumnOptions = useMemo(() => {
    let sourceColumns = sourceTable?.GetColumns().filter((c) => !EXCLUDED_COLUMN_TYPES.includes(c.type)) || []

    if (isEdit) {
      // add any standard lookup column from queryTable if exist
      const queryTableStandardLookup = activeTable?.settings._Columns
        .filter((c) => Boolean(c._Lookup._Standard))
        .map((c) => {
          const fCol = getLookupResolvedColumn(c, activeTable, tablesMap)
          return {
            ...c,
            _ResolvedType: fCol?._Type,
          }
        })

      if (queryTableStandardLookup) {
        sourceColumns = [...sourceColumns, ...fromSdkColumnList(queryTableStandardLookup)]
      }
    }

    return sourceColumns
  }, [sourceTable, isEdit, activeTable, tablesMap])

  /**
   * sort column options
   */
  const sortColumnOptions = useMemo(() => {
    // base options
    const baseOptions = [
      {
        id: '',
        type: 'table',
      },
      {
        id: '#random#',
        type: 'random',
      },
      '#div#',
      `#{${t('Sort By Column')}}#`,
    ] as unknown as ColumnAdapter[]

    // // original (query table) columns
    // const queryTableColumns = isEdit
    //   ? (activeTable?.GetColumns().filter((c) => c.type !== ColumnTypes.Connection) ?? [])
    //   : (sourceTable?.GetColumns().filter((c) => !EXCLUDED_COLUMN_TYPES.includes(c.type)) ?? [])

    // source columns
    const sourceColumns = sourceTable?.GetColumns().filter((c) => !EXCLUDED_COLUMN_TYPES.includes(c.type)) ?? []

    return [...baseOptions, ...sourceColumns]
    //
  }, [sourceTable, t])

  /**
   * reset state
   */
  const reset = useCallback(() => {
    setShowQueryTable(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const navigateToNewTable = useCallback(
    (tableId: string) => {
      if (orgId) {
        navigate(`${ORG_ROUTE}/${orgId}/${ORG_DATABASE_ROUTE}/${dbId}/${tableId}`)
      } else if (isProject) {
        navigate(`${PROJECT_ROUTE}/${project?.id}/${ORG_DATABASE_ROUTE}/${tableId}`)
      }
    },
    [orgId, isProject, navigate, dbId, project?.id]
  )

  const handleSubmit = () => {
    if (isEdit) {
      setShowConfirmModal(true)
    } else {
      handleConfirmSubmit()
    }
  }

  const handleCloseConfirmation = async () => {
    if (loading) return

    setShowConfirmModal(false)
  }

  /**
   * Submit table on confirm
   */
  const handleConfirmSubmit = async () => {
    if (!dbClient || !activeTable || !sourceTable) return

    setLoading(true)
    try {
      // Determine sort order
      const sortOrder = querySettings.sortType === 'desc' ? TableSortOrder.Desc : TableSortOrder.Asc

      // Determine sort by type
      let sortBy = TableSortBy.Column
      if (querySettings.sortBy === '') {
        sortBy = TableSortBy.Default
      } else if (querySettings.sortBy === '#random#') {
        sortBy = TableSortBy.Random
      }

      let filteredColumnIDs = querySettings.columns

      // Validate columnIDs, make sure no advanced columns of source table are included
      if (sourceTable) {
        const invalidColumns = filteredColumnIDs.filter((col) =>
          EXCLUDED_COLUMN_TYPES.includes(sourceTable.columnsMap.get(col)!._Type)
        )
        if (invalidColumns.length > 0) {
          console.warn('Invalid columns found in query table', invalidColumns)
          filteredColumnIDs = filteredColumnIDs.filter((col) => !invalidColumns.includes(col))
        }
      }

      // Convert date filter values from table timezone to UTC before saving
      const utcFilter = convertQueryDates(queryFilter, timezone, 'toUtc')

      let payload: ITableSnapshotSettings = {
        _RefreshRate: querySettings.refreshRate,
        _Source: querySettings.sourceTable,
        _Columns: filteredColumnIDs,
        _Lang: DEFAULT_LANGUAGE,
        _Limit: querySettings.limit,
        _Query: {
          _Op: utcFilter._Op,
          _Statements: utcFilter._Statements,
        },
        _Sort: {
          _By: sortBy,
          _Column:
            querySettings.sortBy === TableSortBy.Default || querySettings.sortBy === '#random#'
              ? ''
              : querySettings.sortBy,
          _Order: sortOrder,
        },
        _Process: {
          _LastRanAt: new Date(),
          _LastRanErr: '',
          _State: ProcessState.Created,
        },
      }

      if (payload._Sort._By === TableSortBy.Column && payload._Sort._Column === '') {
        payload._Sort._By = TableSortBy.Default
      }
      if (payload._Sort._By === TableSortBy.Column && !sortColumnOptions.find((c) => c.id === payload._Sort._Column)) {
        payload._Sort._By = TableSortBy.Default
        payload._Sort._Column = ''
      }

      if (isEdit) {
        await activeTable?.UpdateQuerySettings(payload, timezone)
        window.dispatchEvent(new CustomEvent('db-client-reload'))
        toast.success('Query table updated!')
        //
      } else {
        const newTable = await dbClient.CreateSnapshotOrQueryTable(
          'query',
          querySettings.tableName.trim(),
          payload,
          timezone
        )
        window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_CREATED, { detail: newTable.id }))
        navigateToNewTable(newTable.id)
        toast.success('Query table created!')
      }

      reset() // Close modal and reset form
      //
    } catch (error) {
      console.error('Failed to create query table:', error)
      toast.error('Something went wrong!')
    } finally {
      setLoading(false)
    }
  }

  /**
   * Disabled state
   */
  const disabled = useMemo(() => {
    return querySettings.columns.length < 1 || !sourceTable || !querySettings.tableName.trim() || nameError !== ''
  }, [querySettings.columns, sourceTable, querySettings.tableName, nameError])

  return (
    <>
      <Modal fix show={showModal && !showConfirmModal} onHide={() => reset()} closeOnClickOutside={false}>
        <Modal.Header
          onCloseClick={() => {
            reset()
          }}
        >
          <div className='flex-c gap-2'>
            <MaterialIcon size={16} icon={appIconKeys.tableQuery} className='text-primary-500' />
            <Trans>{isEdit ? 'Edit Query' : 'Query Builder'}</Trans>
          </div>
        </Modal.Header>

        <Modal.Body className='modal-md min-h-[40vh] max-h-[70vh] overflow-y-auto'>
          <div>
            <Trans>
              Query Tables let you create a live, filtered view of your source data with sorting and row limits for
              performance. Only source columns are available (no calculated fields), and any edits sync directly to the
              source table. Ideal for dynamic lists, dashboards, and fast-loading components.
            </Trans>
          </div>

          <Divider />

          <div className='flex flex-col'>
            {!isEdit && (
              <Field>
                <Lbl required>
                  <Trans>Query Table Name</Trans>
                </Lbl>
                <SimpleInput
                  error={t(nameError)}
                  autoFocus={true}
                  required
                  placeholder='Enter query table name'
                  value={querySettings.tableName}
                  onChange={(e) => {
                    const val = e.currentTarget.value
                    setNameError(validateTableName(val, tables))
                    setQuerySettings({ ...querySettings, tableName: val })
                  }}
                />
              </Field>
            )}

            <Field>
              <Lbl>Source</Lbl>
              <div className='w-full'>
                <TablesDropdownV2
                  showPlaceholder
                  value={querySettings.sourceTable}
                  onChange={(value) => {
                    if (querySettings.sourceTable === value) {
                      return
                    }

                    const table = tablesMap.get(value)
                    if (!table) return

                    setQuerySettings((prev) => ({
                      ...prev,
                      sourceTable: value,
                      columns:
                        table
                          ?.GetColumns()
                          .filter((c) => !EXCLUDED_COLUMN_TYPES.includes(c.type))
                          .map((c) => c.id) || [],
                    }))

                    setQueryFilter(initialQueryFilter)
                  }}
                  filterOption={(tables) => {
                    return tables.filter((tb) => !tb.isSnapshot && !tb.isQuery)
                  }}
                />
              </div>
            </Field>

            {sourceTable && (
              <>
                <Field>
                  <Lbl>
                    <Trans>Columns</Trans>
                  </Lbl>

                  <ColumnsSelectToggle
                    table={sourceTable}
                    values={querySettings.columns}
                    onChange={handleColumnChange}
                    excludedColumns={EXCLUDED_COLUMN_TYPES}
                  />
                </Field>

                {/* Query Filter */}
                <SnapshotFilterCondition
                  value={queryFilter}
                  onChange={setQueryFilter}
                  sourceColumns={filterColumnOptions}
                  sourceTable={fromSdkTable(sourceTable.settings)}
                />

                <Field>
                  <Lbl>
                    <Trans>Sort By</Trans>
                  </Lbl>
                  <SelectDropdown
                    id='query-builder-sort-by'
                    withSearch={sortColumnOptions.length > SHOW_SEARCH_LIMIT}
                    portalMenu
                    menuContainerClassName='mk-ag-sdd'
                    menuClassName='min-w-[260px] text-m-m max-h-[200px] overflow-y-auto'
                    menuDefaultSize={false}
                    menuFullWidth={false}
                    menuZIndex='z-401'
                    value={sortColumnOptions.find(({ id }) => id === querySettings.sortBy)}
                    onChange={async (opt) => {
                      const val = (opt as ColumnAdapter)?.id
                      setQuerySettings({ ...querySettings, sortBy: val })
                    }}
                    options={sortColumnOptions}
                    optionLabel={(opt) => `${opt?.id} ${opt?.name} ${opt?.type}`}
                    renderOption={(opt, inToggle) => {
                      if (opt?.id === '' || (inToggle && typeof opt === 'string')) {
                        return (
                          <div className='flex-c'>
                            <MaterialIcon size={16} icon={appIconKeys.table} className='shrink-0' />
                            <Trans>Table Order</Trans>
                          </div>
                        )
                      }
                      if (opt?.id === '#random#') {
                        return (
                          <div className='flex-c'>
                            <MaterialIcon size={16} icon='shuffle' className='shrink-0' />
                            <Trans>Random</Trans>
                          </div>
                        )
                      }

                      return (
                        <RenderColumn
                          withStyle={false}
                          col={opt as ColumnAdapter}
                          className={clsx((opt as ColumnAdapter)?.id === '' && 'text-light')}
                        />
                      )
                    }}
                  />
                </Field>

                <Field>
                  <Lbl>
                    <Trans>Sort Type</Trans>
                  </Lbl>
                  <SingleIconSelect
                    onClick={(opt) => {
                      setQuerySettings({ ...querySettings, sortType: opt.id as 'asc' | 'desc' })
                    }}
                    options={[
                      { id: 'asc', icon: <Asc16 />, title: t('Ascending') },
                      { id: 'desc', icon: <Desc16 />, title: t('Descending') },
                    ]}
                    selected={querySettings.sortType}
                  />
                </Field>

                <Field>
                  <Lbl>
                    <Trans>Limit</Trans>
                  </Lbl>
                  <SelectDropdown
                    onChange={(opt) => {
                      setQuerySettings({ ...querySettings, limit: opt })
                    }}
                    value={querySettings.limit}
                    portalMenu
                    menuContainerClassName='mk-ag-sdd'
                    menuClassName='min-w-[260px] text-m-m max-h-[200px] overflow-y-auto'
                    menuDefaultSize={false}
                    menuFullWidth={false}
                    menuZIndex='z-305'
                    options={LIMIT_OPTIONS}
                    renderOption={(opt) => {
                      return <div>{opt}</div>
                    }}
                  />
                </Field>

                <Field>
                  <Lbl>
                    <Trans>Timezone</Trans>
                  </Lbl>

                  <TzDd id='query-builder-timezone' value={timezone} onChange={setTimezone} withAuto={false} />
                </Field>
              </>
            )}
          </div>
        </Modal.Body>

        <ModalGridFooter
          onHide={reset}
          onCancel={reset}
          onSubmit={handleSubmit}
          cancelLabel={t('Cancel')}
          primaryLabel={isEdit ? t('Save') : t('Create')}
          disabled={disabled}
          isLoading={loading}
        />
      </Modal>

      {isEdit && (
        <Modal fix show={showConfirmModal} onHide={handleCloseConfirmation} closeOnClickOutside={false}>
          <Modal.Header onCloseClick={handleCloseConfirmation}>
            <div className='flex-c gap-2'>
              <MaterialIcon size={16} icon={appIconKeys.tableQuery} className='text-primary-500' />
              <Trans>Confirmation</Trans>
            </div>
          </Modal.Header>

          <Modal.Body className='modal-md min-h-[175px] overflow-y-auto'>
            <div className='font-semibold mb-2'>Are you sure you want to update this query?</div>
            <div>This will update query settings and reload the current table to recompute the data.</div>
          </Modal.Body>

          <ModalGridFooter
            onHide={handleCloseConfirmation}
            onCancel={handleCloseConfirmation}
            onSubmit={handleConfirmSubmit}
            cancelLabel={t('Go Back')}
            primaryLabel={t('Confirm')}
            colorStyle={ButtonColor.Secondary}
            disabled={disabled}
            isLoading={loading}
          />
        </Modal>
      )}
    </>
  )
}
